import React, { useEffect, useState } from 'react'
import clsx from 'clsx'
import CssBaseline from '@material-ui/core/CssBaseline'
import {
  AppBar,
  Toolbar,
  Drawer,
  Divider,
  Typography,
  Grid,
  Box,
  Hidden,
  IconButton,
} from '@material-ui/core'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { SubLink } from 'containers'
import MenuIcon from '@material-ui/icons/Menu'
import HelpOutlineIcon from '@material-ui/icons/HelpOutline'
import Logo from 'assets/images/coachbetter_Logo_Black.webp'
import { withLocalize } from 'react-localize-redux'
import { SeasonSelector, Sidebar, TopNavTeamDropdown } from './components'
import { LogoImage, useStyles } from './styledComponents'

const HELP_URLS = {
  en: 'https://coachbetter.valuecase.com/spaces/coachbetter_2?token=6859d42518a824d2',
  de: 'https://coachbetter.valuecase.com/spaces/coachbetter?token=a8bb44206b7d7f90',
}

const NavBar = ({
  children,
  match,
  onlyTeamSelector = false,
  currentTeam,
  currentClub,
  activeLanguage: { code: locale },
}) => {
  const classes = useStyles()
  const {
    history,
    params: { teamUuid },
  } = match
  const [open, setOpen] = React.useState(false)
  const [clubTheme, setClubTheme] = useState(null)

  const handleDrawerOpen = () => {
    setOpen(true)
  }

  const handleDrawerClose = () => {
    setOpen(false)
  }

  if (teamUuid && !currentTeam) {
    history.push('/teams')
  }

  useEffect(() => {
    if (currentClub) {
      let clubTheme = {}
      if (currentClub.theme_temp) {
        try {
          clubTheme = JSON.parse(currentClub.theme_temp) ?? {}
        } catch (error) {
          console.error('Error parsing JSON:', error)
        }
      }
      setClubTheme(clubTheme)
    }
  }, [currentClub])

  const handleHelpClick = () => {
    const helpUrl = HELP_URLS[locale] || HELP_URLS.en
    window.open(helpUrl, '_blank')
  }

  return (
    <div>
      <div className={classes.root}>
        <CssBaseline />
        <AppBar
          position="fixed"
          className={clsx(classes.appBar, {
            [classes.appBarShift]: open,
          })}
          elevation={0}
        >
          <Toolbar className={classes.ToolbarNav} elevation={0}>
            {currentClub && clubTheme && (
              <Hidden only={['md', 'lg', 'xl']}>
                <IconButton
                  aria-label="open drawer"
                  onClick={handleDrawerOpen}
                  className={clsx({
                    [classes.hide]: open,
                  })}
                  style={{ zIndex: 2000 }}
                >
                  <MenuIcon />
                </IconButton>
              </Hidden>
            )}
            <div className={classes.logo}>
              <SubLink to="/">
                <LogoImage
                  src={clubTheme ? clubTheme.logo : Logo}
                  alt="Coachbetter"
                  className={classes.brand}
                />
              </SubLink>
            </div>
            <Hidden only={['xs', 'sm']}>
              <div className={classes.logo}>
                <Typography
                  variant="h6"
                  className={classes.headTypography}
                  component="h6"
                >
                  {currentTeam.name}
                </Typography>
              </div>
              <Grid item className={classes.borderRight}>
                {!onlyTeamSelector && <SeasonSelector />}
                <HelpOutlineIcon
                  className={classes.helpIcon}
                  onClick={handleHelpClick}
                />
              </Grid>
            </Hidden>
            <Grid item>
              <TopNavTeamDropdown />
            </Grid>
          </Toolbar>
        </AppBar>

        {window.location.pathname === '/teams' ? null : (
          <>
            <Hidden only={['xs', 'sm']} className={classes.hide}>
              <Drawer
                className={classes.drawer}
                variant="permanent"
                classes={{
                  paper: classes.drawerPaper,
                  elevation: 0,
                }}
              >
                <div className={classes.drawerContainer}>
                  <Sidebar onClose={handleDrawerClose} />
                </div>
              </Drawer>
            </Hidden>
            <Hidden only={['md', 'lg', 'xl']}>
              <Drawer
                className={classes.drawerMobile}
                ModalProps={{
                  keepMounted: true,
                  disableScrollLock: true,
                }}
                variant="persistent"
                anchor="left"
                open={open}
                classes={{
                  paper: classes.drawerMobilePaper,
                  elevation: 0,
                }}
                onClose={handleDrawerClose}
              >
                <Box>
                  <IconButton
                    aria-label="close drawer"
                    onClick={handleDrawerClose}
                    edge="end"
                    style={{ zIndex: 1000 }}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                </Box>
                <Divider />
                <Box pt={2.6} pb={1} px={2.3}>
                  {window.location.pathname === '/teams' ? null : (
                    <>{!onlyTeamSelector && <SeasonSelector />}</>
                  )}
                </Box>
                <Divider />
                <div className={classes.drawerContainer}>
                  <Sidebar
                    onClose={handleDrawerClose}
                    onKeyDown={handleDrawerClose}
                  />
                </div>
              </Drawer>
            </Hidden>
          </>
        )}
        <main
          className={clsx(classes.content, {
            [classes.contentShift]: open,
          })}
        >
          {children}
        </main>
      </div>
    </div>
  )
}

NavBar.defaultProps = {
  onlyTeamSelector: false,
}

NavBar.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.element.isRequired,
    PropTypes.arrayOf(PropTypes.element.isRequired).isRequired,
  ]).isRequired,
  onlyTeamSelector: PropTypes.bool,
  match: PropTypes.shape().isRequired,
  currentTeam: PropTypes.shape().isRequired,
  currentClub: PropTypes.shape({
    theme_temp: PropTypes.string,
  }).isRequired,
  activeLanguage: PropTypes.shape({
    code: PropTypes.string.isRequired,
  }).isRequired,
}

export default compose(
  connect(({ team, clubs }) => ({
    currentTeam: team,
    currentClub: clubs.current,
  })),
  withRouter,
  withLocalize
)(NavBar)
